import { useEffect, useState } from 'react';
import type { HallView } from '@versesverse/core';
import { useLang } from '../i18n/lang';

/**
 * WHICH HALLS THIS READER MAY ACT UNDER.
 *
 * `halls/mine` filters to ACTIVE memberships, which is what makes this list exactly the set the
 * server's `mayOpenInHall` will accept — a pending member is offered nothing that would then be
 * refused. The alignment is not enforced by a type, so it is stated here where someone would
 * otherwise have to go and check.
 */
export interface HallMembershipReader {
  myHalls(): Promise<HallView[]>;
}

export interface HallPickerProps {
  value: string;
  onChange(hallId: string): void;
  halls: HallView[];
  className: string;
  /**
   * What this picker is qualifying, in the reader's words.
   *
   * Passed in rather than fixed, because BOTH pickers appear on a node at once — the rooms panel
   * renders the sessions panel inside itself. Two controls labelled identically on one screen is
   * ambiguous to anyone and genuinely unusable with a screen reader, so a room is "opened under" a
   * Hall and a study is "held under" one.
   */
  label: string;
  /**
   * Whether the reader's communities COULD NOT BE READ, which is not the same as having none.
   *
   * See `useMyHalls` for what the silence used to cost. Optional so a caller that has no such
   * fact — a test, or a screen that was handed a list — behaves exactly as before.
   */
  unread?: boolean;
}

/**
 * "Open it under…" — the control that turns a commons room or session into a community's.
 *
 * Rendered by both the rooms panel and the sessions panel, which is why it is a component rather
 * than the same nine lines twice: the rule about when it appears, and the fact that the commons is
 * the default, are decisions that must not drift apart between the two places a reader meets them.
 *
 * IT RENDERS NOTHING for a reader in no Hall — which is nearly everyone. A control that is always
 * present for a case that rarely applies is the cost this design refuses to pay, and the caller
 * therefore does not need to guard the call site.
 */
export function HallPicker({ value, onChange, halls, className, label, unread }: HallPickerProps): React.JSX.Element | null {
  const { t } = useLang();
  /*
   * AN EMPTY LIST AND AN UNREAD ONE LOOK THE SAME AND ARE NOT.
   *
   * Rendering nothing is right for the reader who is in no community — nearly everyone. For the
   * leader whose read FAILED it is a wrong answer wearing the right shape: the control is missing,
   * they open the room anyway, and it quietly belongs to nobody. Their co-hosts cannot moderate
   * it, their community's ban does not reach it, and it is not on their Hall's page.
   */
  if (halls.length === 0) {
    return unread ? <p className="halls-unread">{t('halls.couldNotReadYours')}</p> : null;
  }

  return (
    <select
      className={className}
      value={value}
      onChange={event => onChange(event.target.value)}
      aria-label={label}
    >
      {/* The commons is first and is the default, so speaking under a community's name is an act
          rather than a setting a member forgets is switched on. */}
      <option value="">{t('rooms.commons')}</option>
      {halls.map(hall => (
        <option key={hall.id} value={hall.id}>{hall.name}</option>
      ))}
    </select>
  );
}

/**
 * The reader's own Halls, fetched ONCE per sign-in rather than per node.
 *
 * Which communities a person belongs to does not change as they walk the map, and this would
 * otherwise be a request on every node they touch. Signed-out readers never ask at all.
 *
 * A FAILURE USED TO BE SILENT, and the argument for it was half right. Opening a commons room is
 * the path everyone uses and must not depend on this having succeeded — that part still holds, and
 * nothing here blocks. What the argument missed is that the control MISSING is only a visible
 * difference to somebody who knew it should be there. A leader whose read failed opens the room
 * anyway, and it quietly belongs to nobody: their co-hosts cannot moderate it, their community's
 * ban does not reach it, and it is not on the Hall's page. So the failure is now said, in the place
 * the control would have been, and the commons path is untouched.
 *
 * `client` MUST KEEP ITS IDENTITY between renders. It is a dependency of the effect, so a caller
 * building it inline — `useMyHalls({ myHalls }, meId)` — re-reads, sets state and renders again for
 * ever. The app builds one `SocialClient` in a `useMemo` and is fine; the first test written for
 * this hook was not, and it did not fail, it killed the worker. Said here because the type cannot.
 */
/** The reader's communities, and whether the attempt to read them failed. */
export interface MyHalls {
  halls: HallView[];
  /** True when the read failed — an empty list nobody should read as "you are in none". */
  unread: boolean;
}

export function useMyHalls(client: HallMembershipReader, meId: string | null): MyHalls {
  const [halls, setHalls] = useState<HallView[]>([]);
  const [unread, setUnread] = useState(false);

  useEffect(() => {
    if (meId === null) { setHalls([]); setUnread(false); return; }
    let live = true;
    client.myHalls()
      .then(found => { if (live) { setHalls(found); setUnread(false); } })
      .catch(() => { if (live) setUnread(true); });
    return () => { live = false; };
  }, [client, meId]);

  return { halls, unread };
}
